import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useSearchPatient } from '@/features/patients/hooks/useSearchPatient'
import { isApiError } from '@/utils/error'
import { showError } from '@/utils/toast'
import { Loader2, Search } from 'lucide-react'
import { forwardRef, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PatientSearchResult } from '../../types/patient'

type Props = {
  onResult: (result: PatientSearchResult) => void
  onLoading?: (loading: boolean) => void
  onSearchStart?: () => void
  placeholder?: string
  autoFocus?: boolean
  className?: string
  testId?: string
}

export type PatientSearchRef = {
  reset: () => void
}

export const PatientSearch = forwardRef<PatientSearchRef, Props>(
  ({ onResult, onLoading, onSearchStart, placeholder, autoFocus = true, className, testId }, ref) => {
    const { t } = useTranslation()

    const [search, setSearch] = useState('')
    const searchPatient = useSearchPatient()

    useImperativeHandle(ref, () => ({
      reset() {
        setSearch('')
      },
    }))

    async function handleSearch() {
      if (!search.trim()) return

      onSearchStart?.()
      onLoading?.(true)
      try {
        const result = await searchPatient.mutateAsync(search)
        onResult(result)
      } catch (error) {
        if (isApiError(error)) {
          showError(error.message)
        }
      } finally {
        onLoading?.(false)
      }
    }

    return (
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />

          <Input
            data-testid={testId}
            autoFocus={autoFocus}
            placeholder={placeholder ?? t('common.placeholder.searchPatient')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleSearch()
              }
            }}
            className="pl-8"
          />
        </div>

        <Button data-testid={testId ? `${testId}-search-button` : undefined} onClick={handleSearch} disabled={!search.trim() || searchPatient.isPending}>
          {searchPatient.isPending && <Loader2 className="size-4 animate-spin" />}
          {t('common.search')}
        </Button>
      </div>
    )
  },
)
